import React, { createContext, useContext, useState, useMemo } from 'react';

export type DebugRole = 'real' | 'admin' | 'customer';

interface DebugRoleContextValue {
  /** The currently selected debug role ('real' = use actual backend values) */
  debugRole: DebugRole;
  /** Update the debug role */
  setDebugRole: (role: DebugRole) => void;
  /** Whether a debug override is active (not 'real') */
  isOverrideActive: boolean;
  /** Whether the clarifications feature is enabled (dev toggle) */
  clarificationsEnabled: boolean;
  /** Toggle clarifications feature visibility */
  setClarificationsEnabled: (enabled: boolean) => void;
}

const DebugRoleContext = createContext<DebugRoleContextValue>({
  debugRole: 'real',
  setDebugRole: () => {},
  isOverrideActive: false,
  clarificationsEnabled: true,
  setClarificationsEnabled: () => {},
});

export const DebugRoleProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [debugRole, setDebugRole] = useState<DebugRole>('real');
  const [clarificationsEnabled, setClarificationsEnabled] = useState(true);

  const value = useMemo(
    () => ({
      debugRole,
      setDebugRole,
      isOverrideActive: debugRole !== 'real',
      clarificationsEnabled,
      setClarificationsEnabled,
    }),
    [debugRole, clarificationsEnabled]
  );

  return (
    <DebugRoleContext.Provider value={value}>
      {children}
    </DebugRoleContext.Provider>
  );
};

export const useDebugRole = (): DebugRoleContextValue => {
  return useContext(DebugRoleContext);
};
